import styled, { css } from 'styled-components'
import { Button } from '../../atoms/Button'

export const AnalysisFilterSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  flex-shrink: 0;
`

export const AnalysisFilterBar = styled.div`
  padding: 16px 0px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 54px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`

export const AnalysisLocationFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  flex-wrap: wrap;
`

export const AnalysisFilterChip = styled.div<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary + '20' : theme.colors.background.secondary};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.primary)};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border.light)};
  border-radius: 16px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  user-select: none;

  &:hover {
    background: ${({ theme, $active }) =>
      $active ? theme.colors.primary + '30' : theme.colors.background.tertiary};
    border-color: ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.border.default};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 11px;
    height: 11px;
  }
`

export const AnalysisLocationBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;

  svg {
    width: 10px;
    height: 10px;
  }
`

export const AnalysisFilterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

export const AnalysisFilterToggleButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 6px;
`

export const AnalysisActiveFiltersCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 5px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  margin-left: 3px;
`

export const AnalysisAdvancedFiltersPanel = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  border-top: ${({ theme, $isOpen }) =>
    $isOpen ? `1px solid ${theme.colors.border.light}` : 'none'};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    /* Bottom sheet overlay on mobile */
    ${({ $isOpen }) =>
      $isOpen &&
      css`
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1000;
        background: white;
        border-top: none;
        border-radius: 16px 16px 0 0;
        box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
        max-height: 70vh;
        overflow-y: auto;
        animation: slideUp 0.25s ease-out;
      `}
  }

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`

export const AnalysisAdvancedFiltersContent = styled.div`
  padding: 16px 20px;
  background: ${({ theme }) => theme.colors.background.secondary + '50'};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 16px;
    background: white;
  }
`

export const AnalysisDivider = styled.div`
  width: 1px;
  height: 13px;
  background: ${({ theme }) => theme.colors.border.light};
`

/* ============================================
 * Mobile-specific compact filter row
 * ============================================ */

export const MobileFilterRow = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
  }
`

export const MobileLocationButton = styled.button<{ $active?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border.light)};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  min-width: 0;
  transition: all 0.2s ease;

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.text.tertiary};
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &::after {
    content: '\\25BE';
    margin-left: auto;
    color: ${({ theme }) => theme.colors.text.tertiary};
    flex-shrink: 0;
    font-size: 10px;
  }
`

export const MobileFilterButton = styled.button<{ $hasFilters?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 7px 10px;
  border-radius: 8px;
  background: ${({ theme, $hasFilters }) =>
    $hasFilters ? theme.colors.primary + '10' : theme.colors.background.secondary};
  border: 1px solid
    ${({ theme, $hasFilters }) => ($hasFilters ? theme.colors.primary : theme.colors.border.light)};
  font-size: 11px;
  color: ${({ theme, $hasFilters }) =>
    $hasFilters ? theme.colors.primary : theme.colors.text.primary};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  flex-shrink: 0;

  svg {
    width: 12px;
    height: 12px;
  }
`

export const MobileFilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 600;
`

export const MobileSortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 7px 10px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &::after {
    content: '\\25BE';
    color: ${({ theme }) => theme.colors.text.tertiary};
    font-size: 10px;
  }
`

/* Bottom sheet backdrop */
export const MobileFilterBackdrop = styled.div<{ $visible: boolean }>`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: ${({ $visible }) => ($visible ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 999;
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

/* Bottom sheet header */
export const MobileSheetHeader = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 16px 12px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};

    h3 {
      font-size: 15px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.text.primary};
      margin: 0;
    }
  }
`

/* Location options in bottom sheet */
export const MobileLocationSheet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    padding: 8px 0;
  }
`

export const MobileLocationOption = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: ${({ $active }) => ($active ? 'rgba(2, 148, 223, 0.06)' : 'transparent')};
  border: none;
  font-size: 14px;
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.primary)};
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:active {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.tertiary)};
  }
`
